import type { LumaSize, LumaSubcategory } from './types';

/**
 * Applies size filtering rules on top of the sizes already pre-filtered by the server.
 *
 * The server (ConfiguratorEndpoints::getConfig) has already applied:
 *   1. Subcategory dimension constraints
 *   2. Artwork aspect ratio matching
 *
 * The pre-filtered sizes are available on each LumaSubcategory.availableSizes. This
 * module provides helper utilities used for display and label formatting on the client.
 *
 * Client-side aspect ratio re-filtering is available as a fallback if the image ratio
 * was not computable server-side (e.g. image metadata missing).
 */
export class SizeFilter {

	private static readonly ASPECT_TOLERANCE = 0.01; // 1% — matches Lumaprints API requirement

	/**
	 * Filter sizes by aspect ratio. Both orientations (portrait/landscape) of the size
	 * are tested against the image ratio. Sizes where either orientation matches are kept.
	 */
	static filterByAspectRatio(sizes: LumaSize[], imageRatio: number): LumaSize[] {
		return sizes.filter((size) => {
			if (size.width === 0 || size.height === 0) return false;

			const naturalRatio = size.width / size.height;
			const flippedRatio = size.height / size.width;

			const naturalDiff = Math.abs(naturalRatio - imageRatio) / Math.max(naturalRatio, imageRatio);
			const flippedDiff = Math.abs(flippedRatio - imageRatio) / Math.max(flippedRatio, imageRatio);

			return naturalDiff <= this.ASPECT_TOLERANCE || flippedDiff <= this.ASPECT_TOLERANCE;
		});
	}

	/**
	 * Filter sizes by a subcategory's min/max dimension constraints.
	 * Checked in both orientations.
	 */
	static filterByDimensions(sizes: LumaSize[], subcat: LumaSubcategory): LumaSize[] {
		return sizes.filter((size) => {
			const { width: w, height: h } = size;

			const fitsNatural = w >= subcat.minWidth && w <= subcat.maxWidth && h >= subcat.minHeight && h <= subcat.maxHeight;
			const fitsFlipped = h >= subcat.minWidth && h <= subcat.maxWidth && w >= subcat.minHeight && w <= subcat.maxHeight;

			return fitsNatural || fitsFlipped;
		});
	}

	/**
	 * Format a size as a display label, e.g. "8 × 10 in"
	 */
	static formatLabel(size: LumaSize): string {
		const formatNum = (n: number) => Number.isInteger(n) ? n.toString() : n.toFixed(1).replace(/\.0$/, '');

		return `${formatNum(size.width)} × ${formatNum(size.height)} in`;
	}

	/**
	 * Check if two sizes are equal (order-insensitive for display purposes).
	 */
	static isSameSize(a: LumaSize, b: LumaSize): boolean {
		return a.width === b.width && a.height === b.height;
	}

	/** Stable token for `<option value>` on size dropdowns (matches server width/height). */
	static encodeSizeValue(size: LumaSize): string {
		return `${size.width}x${size.height}`;
	}

	static findSizeByValue(sizes: LumaSize[], value: string): LumaSize | undefined {
		return sizes.find((s) => this.encodeSizeValue(s) === value);
	}
}
